import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useGamificationStore } from '../stores/gamificationStore';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Target,
  Scale,
  Lightbulb,
  Heart,
  Eye,
  Users,
  Mountain,
  Download,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { PROFILES } from '../data/assessmentProfiles';

// Assessment questions designed using psychometric best practices
// - Forced-choice scenarios (all options equally valid)
// - Behavioral preferences over self-assessment
// - Less transparent scoring
// - Concrete situations over abstract descriptions
const QUESTIONS = [
  {
    id: 1,
    text: 'You\'re at dinner and someone makes a strong claim about climate change. You find yourself naturally drawn to...',
    options: [
      { text: 'Wondering what research or data backs this up', scores: { truthSeeker: 3, logicMaster: 1 } },
      { text: 'Thinking about the implications for future generations', scores: { moralCompass: 2, justiceAdvocate: 2 } },
      { text: 'Noticing whether their reasoning follows logically', scores: { logicMaster: 3 } },
      { text: 'Questioning what worldview assumptions they\'re making', scores: { criticalThinker: 3, truthSeeker: 1 } }
    ]
  },
  {
    id: 2,
    text: 'You have a free afternoon to watch something educational. You choose a documentary about...',
    options: [
      { text: 'How memories work and whether you\'re still the same person from childhood', scores: { identityExplorer: 3 } },
      { text: 'The latest AI developments and what consciousness might be', scores: { consciousnessInvestigator: 3 } },
      { text: 'Prison reform and what justice really means', scores: { justiceAdvocate: 3 } },
      { text: 'People who left successful careers to pursue passion projects', scores: { meaningMaker: 2, identityExplorer: 1 } }
    ]
  },
  {
    id: 3,
    text: 'A friend shares a viral social media post. Before deciding whether to share it, you...',
    options: [
      { text: 'Check who originally posted it and their credentials', scores: { truthSeeker: 3 } },
      { text: 'Look for whether the reasoning holds up', scores: { logicMaster: 3 } },
      { text: 'Consider what effect spreading this might have', scores: { moralCompass: 2, justiceAdvocate: 1 } },
      { text: 'Ask yourself what biases might be hiding in it', scores: { criticalThinker: 3 } }
    ]
  },
  {
    id: 4,
    text: 'You\'re reading a novel and find one character particularly fascinating because they...',
    options: [
      { text: 'Make unexpected choices that seem to defy expectations', scores: { identityExplorer: 2, meaningMaker: 1 } },
      { text: 'Experience sensations and feelings in vivid, unusual ways', scores: { consciousnessInvestigator: 3 } },
      { text: 'Rebel against unjust systems despite personal cost', scores: { justiceAdvocate: 3, moralCompass: 1 } },
      { text: 'Create their own purpose in a chaotic world', scores: { meaningMaker: 3 } }
    ]
  },
  {
    id: 5,
    text: 'In a group project, conflict arises. You find yourself focusing on...',
    options: [
      { text: 'Whether people are actually listening to each other\'s reasoning', scores: { logicMaster: 2, criticalThinker: 1 } },
      { text: 'Whether the disagreement is based on facts or assumptions', scores: { truthSeeker: 2, criticalThinker: 1 } },
      { text: 'Ensuring everyone is treated fairly in the process', scores: { justiceAdvocate: 3, moralCompass: 1 } },
      { text: 'What unstated beliefs are causing the tension', scores: { criticalThinker: 3 } }
    ]
  },
  {
    id: 6,
    text: 'You come across an interesting article that challenges something you\'ve always believed. Your first reaction is...',
    options: [
      { text: 'Curiosity about what evidence they present', scores: { truthSeeker: 3 } },
      { text: 'Interest in how this affects what\'s right and wrong', scores: { moralCompass: 3 } },
      { text: 'Excitement about re-examining your assumptions', scores: { criticalThinker: 3 } },
      { text: 'Wondering if this changes who you are', scores: { identityExplorer: 2, meaningMaker: 1 } }
    ]
  },
  {
    id: 7,
    text: 'You notice you\'ve been scrolling social media for 30 minutes. The thought that occurs to you is...',
    options: [
      { text: '"Did I actually choose this or am I just programmed to do it?"', scores: { identityExplorer: 3 } },
      { text: '"What am I experiencing right now? Where is my mind?"', scores: { consciousnessInvestigator: 3 } },
      { text: '"Is this how I want to spend my limited time?"', scores: { meaningMaker: 3 } },
      { text: '"Why do I accept what these algorithms show me?"', scores: { criticalThinker: 2, truthSeeker: 1 } }
    ]
  },
  {
    id: 8,
    text: 'A colleague makes a decision you disagree with. You\'re most bothered when...',
    options: [
      { text: 'Their reasoning has obvious holes but they don\'t see it', scores: { logicMaster: 3 } },
      { text: 'They haven\'t checked whether their beliefs are actually true', scores: { truthSeeker: 3 } },
      { text: 'The decision harms some people more than others', scores: { justiceAdvocate: 2, moralCompass: 2 } },
      { text: 'They\'re making choices based on unexamined assumptions', scores: { criticalThinker: 3 } }
    ]
  },
  {
    id: 9,
    text: 'You\'re given a gift card to a bookshop. You head to the section on...',
    options: [
      { text: 'Cognitive science and how we think', scores: { criticalThinker: 2, consciousnessInvestigator: 1 } },
      { text: 'Logic, reasoning, and argumentation', scores: { logicMaster: 3 } },
      { text: 'How to evaluate information and spot misinformation', scores: { truthSeeker: 3 } },
      { text: 'Ethics, morality, and how to live well', scores: { moralCompass: 3 } }
    ]
  },
  {
    id: 10,
    text: 'You wake up from a vivid dream. You find yourself pondering...',
    options: [
      { text: 'Whether "dream you" is the same person as "awake you"', scores: { identityExplorer: 3 } },
      { text: 'What consciousness is and where dreams come from', scores: { consciousnessInvestigator: 3 } },
      { text: 'What the dream might reveal about your values', scores: { moralCompass: 2, meaningMaker: 1 } },
      { text: 'How you can tell dreams from reality', scores: { truthSeeker: 2, criticalThinker: 1 } }
    ]
  },
  {
    id: 11,
    text: 'At a party, you end up in the most engaging conversation when someone brings up...',
    options: [
      { text: 'Whether perfect lie-detector technology should exist', scores: { moralCompass: 2, justiceAdvocate: 1 } },
      { text: 'If you were teleported (destroyed here, rebuilt there), would you survive?', scores: { identityExplorer: 3 } },
      { text: 'Could an AI ever actually feel pain or just simulate it?', scores: { consciousnessInvestigator: 3 } },
      { text: 'How should society\'s resources be distributed?', scores: { justiceAdvocate: 3 } }
    ]
  },
  {
    id: 12,
    text: 'You\'re considering a major life change. The question weighing most heavily is...',
    options: [
      { text: '"What assumptions am I making about what\'s possible?"', scores: { criticalThinker: 3 } },
      { text: '"What evidence do I have that this is the right move?"', scores: { truthSeeker: 2, logicMaster: 1 } },
      { text: '"Is this aligned with my core values?"', scores: { moralCompass: 3 } },
      { text: '"Will this make my life more meaningful?"', scores: { meaningMaker: 3 } }
    ]
  },
  {
    id: 13,
    text: 'You overhear someone use a common saying like "Everything happens for a reason." You think...',
    options: [
      { text: '"What does that even mean? Is it testable?"', scores: { truthSeeker: 2, logicMaster: 1 } },
      { text: '"What worldview must someone hold to believe that?"', scores: { criticalThinker: 3 } },
      { text: '"Do I have control over my life or don\'t I?"', scores: { identityExplorer: 3 } },
      { text: '"Are they creating meaning or avoiding responsibility?"', scores: { meaningMaker: 2, moralCompass: 1 } }
    ]
  },
  {
    id: 14,
    text: 'You\'re watching a debate and feel frustrated because...',
    options: [
      { text: 'Neither side is defining their terms clearly', scores: { criticalThinker: 2, logicMaster: 1 } },
      { text: 'The arguments have obvious logical flaws', scores: { logicMaster: 3 } },
      { text: 'No one is citing credible sources', scores: { truthSeeker: 3 } },
      { text: 'They\'re missing the moral stakes entirely', scores: { moralCompass: 2, justiceAdvocate: 1 } }
    ]
  },
  {
    id: 15,
    text: 'A new technology is announced that can upload memories. Your immediate thought is...',
    options: [
      { text: '"If my memories were in a computer, would that be me?"', scores: { identityExplorer: 3, consciousnessInvestigator: 1 } },
      { text: '"Would the uploaded version be conscious or just a copy?"', scores: { consciousnessInvestigator: 3 } },
      { text: '"Who should have access to this? What about inequality?"', scores: { justiceAdvocate: 3 } },
      { text: '"What would it mean to live knowing you could be copied?"', scores: { meaningMaker: 2, identityExplorer: 1 } }
    ]
  },
  {
    id: 16,
    text: 'You\'re asked to mentor someone younger. You find yourself most wanting to teach them...',
    options: [
      { text: 'How to question what they think they know', scores: { criticalThinker: 3 } },
      { text: 'How to build solid arguments and spot bad ones', scores: { logicMaster: 3 } },
      { text: 'How to distinguish reliable information from noise', scores: { truthSeeker: 3 } },
      { text: 'How to make decisions they won\'t regret', scores: { moralCompass: 2, meaningMaker: 1 } }
    ]
  },
  {
    id: 17,
    text: 'You read about a scientific study with surprising results. You\'re most curious about...',
    options: [
      { text: 'Whether the study design and methodology are sound', scores: { truthSeeker: 3, logicMaster: 1 } },
      { text: 'Whether the conclusion actually follows from the data', scores: { logicMaster: 2, truthSeeker: 1 } },
      { text: 'What this reveals about human nature or identity', scores: { identityExplorer: 2, consciousnessInvestigator: 1 } },
      { text: 'What assumptions the researchers brought to their work', scores: { criticalThinker: 3 } }
    ]
  },
  {
    id: 18,
    text: 'Late at night, you find yourself lying awake thinking about...',
    options: [
      { text: 'Whether you\'re living according to what truly matters to you', scores: { meaningMaker: 3, moralCompass: 1 } },
      { text: 'What makes you the same person you were ten years ago', scores: { identityExplorer: 3 } },
      { text: 'What it would be like to experience life as someone else', scores: { consciousnessInvestigator: 2, identityExplorer: 1 } },
      { text: 'Whether the society you live in is fundamentally fair', scores: { justiceAdvocate: 3 } }
    ]
  }
];

const PhilosophicalProfileAssessment = () => {
  const { setAssessmentResults } = useApp();
  const [view, setView] = useState('intro'); // intro, questions, results
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [profileScores, setProfileScores] = useState({});
  const [previousResults, setPreviousResults] = useState([]);

  // Load previous results
  useEffect(() => {
    const saved = localStorage.getItem('philosophicalProfileResults');
    if (saved) {
      setPreviousResults(JSON.parse(saved));
    }
  }, []);

  const answerQuestion = useCallback((optionIndex) => {
    const question = QUESTIONS[currentQuestionIndex];
    const selectedOption = question.options[optionIndex];

    // Update answers
    setAnswers(prev => ({
      ...prev,
      [question.id]: optionIndex
    }));

    // Update scores
    setProfileScores(prev => {
      const updated = { ...prev };
      Object.entries(selectedOption.scores).forEach(([profile, points]) => {
        updated[profile] = (updated[profile] || 0) + points;
      });
      return updated;
    });

    // Move to next question or results
    if (currentQuestionIndex < QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setView('results');
    }
  }, [currentQuestionIndex]);

  const goBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      // Remove the previous answer from scores
      const previousQuestion = QUESTIONS[currentQuestionIndex - 1];
      const previousAnswerIndex = answers[previousQuestion.id];

      if (previousAnswerIndex !== undefined) {
        const previousOption = previousQuestion.options[previousAnswerIndex];
        setProfileScores(prev => {
          const updated = { ...prev };
          Object.entries(previousOption.scores).forEach(([profile, points]) => {
            updated[profile] = Math.max(0, (updated[profile] || 0) - points);
          });
          return updated;
        });
      }

      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex, answers]);

  const calculateResults = useCallback(() => {
    // Get sorted profiles by score
    const sortedProfiles = Object.entries(profileScores)
      .sort(([, a], [, b]) => b - a)
      .map(([profileId, score]) => ({
        ...PROFILES[profileId],
        score,
        percentage: Math.round((score / Math.max(...Object.values(profileScores))) * 100)
      }));

    return sortedProfiles;
  }, [profileScores]);

  const saveResults = useCallback(() => {
    const results = calculateResults();
    const resultData = {
      date: new Date().toISOString(),
      topProfile: results[0],
      allScores: results,
      answers
    };

    const updated = [resultData, ...previousResults].slice(0, 5); // Keep last 5 results
    localStorage.setItem('philosophicalProfileResults', JSON.stringify(updated));
    setPreviousResults(updated);

    // First completion earns the badge; retakes earn retake points only.
    const gamification = useGamificationStore.getState();
    if (previousResults.length === 0) {
      gamification.completeAssessment();
    } else {
      gamification.retakeAssessment();
    }

    // Feed the Dashboard's assessment card (it reads profileType.name/tagline).
    setAssessmentResults({
      date: resultData.date,
      topProfileId: results[0].id,
      profileType: {
        name: results[0].title,
        tagline: `Recommended starting point: ${results[0].module.replace(/^Module \d+: /, '')}`
      }
    });
  }, [calculateResults, answers, previousResults, setAssessmentResults]);

  const resetAssessment = useCallback(() => {
    setView('intro');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setProfileScores({});
  }, []);

  const exportResults = useCallback(() => {
    const results = calculateResults();
    const exportData = {
      assessmentDate: new Date().toISOString(),
      primaryProfile: results[0]?.title,
      secondaryProfile: results[1]?.title,
      tertiaryProfile: results[2]?.title,
      allScores: results.map(r => ({
        profile: r.title,
        score: r.score,
        percentage: r.percentage
      })),
      recommendedModules: results.slice(0, 3).map(r => r.module),
      recommendedTools: results.slice(0, 3).flatMap(r => r.recommendedTools)
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `philosophical-profile-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [calculateResults]);

  // Save exactly once per completion — saveResults updates previousResults,
  // which changes its own identity; an unguarded effect here loops forever.
  const savedForThisRun = useRef(false);
  useEffect(() => {
    if (view === 'results' && !savedForThisRun.current) {
      savedForThisRun.current = true;
      saveResults();
    }
    if (view !== 'results') {
      savedForThisRun.current = false;
    }
  }, [view, saveResults]);

  // Advancing moves keyboard/screen-reader focus onto the new question, and
  // finishing moves it onto the results heading (WCAG 2.4.3). The timeout
  // covers AnimatePresence mode="wait", which mounts the next view only after
  // the previous one's exit animation.
  const questionHeadingRef = useRef(null);
  const resultsHeadingRef = useRef(null);
  useEffect(() => {
    if (view !== 'questions') return undefined;
    const t = setTimeout(() => questionHeadingRef.current?.focus({ preventScroll: false }), 350);
    return () => clearTimeout(t);
  }, [view, currentQuestionIndex]);
  useEffect(() => {
    if (view !== 'results') return undefined;
    const t = setTimeout(() => resultsHeadingRef.current?.focus({ preventScroll: false }), 350);
    return () => clearTimeout(t);
  }, [view]);

  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const progress = ((currentQuestionIndex) / QUESTIONS.length) * 100;
  const results = view === 'results' ? calculateResults() : [];

  return (
    <div className="text-white p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <AnimatePresence mode="wait">
          {/* Intro View */}
          {view === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Hero: pre-dawn sky backdrop: you're about to find something out */}
              <div className="relative text-center mb-8 rounded-3xl overflow-hidden py-10 px-4">
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                  <img
                    src="/images/cloud-assessment.webp"
                    alt=""
                    className="w-full h-full object-cover object-[50%_35%] opacity-60"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-gunmetal/55 via-gunmetal/30 to-gunmetal" />
                </div>
                <div className="relative">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.6 }}
                    className="flex justify-center mb-6"
                  >
                    <div className="relative">
                      <Brain className="w-24 h-24 text-purple-400" />
                      <Sparkles className="w-8 h-8 text-yellow-400 absolute -top-2 -right-2 animate-pulse" />
                    </div>
                  </motion.div>
                  <h1 className="text-5xl font-bold mb-4">Discover Your Philosophical Profile</h1>
                  <p className="text-xl text-purple-200">
                    What kind of philosophical thinker are you?
                  </p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-purple-500/30">
                <h2 className="text-2xl font-bold mb-4">About This Assessment</h2>
                <div className="text-purple-100 space-y-4">
                  <p>
                    This isn't a test: there are no right or wrong answers. Instead, this assessment
                    reveals which philosophical domains naturally intrigue you and match your
                    thinking patterns.
                  </p>
                  <p>
                    You'll discover your unique philosophical profile across 8 domains:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
                    {Object.values(PROFILES).map(profile => {
                      const Icon = profile.icon;
                      return (
                        <div
                          key={profile.id}
                          className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3"
                        >
                          <Icon className={`w-5 h-5 text-${profile.color}-400`} />
                          <span className="font-medium">{profile.title}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p>
                    <strong>How it works:</strong> Answer 18 scenario-based questions about what
                    fascinates you, frustrates you, and draws your curiosity. We'll map your responses
                    to philosophical domains and give you a personalised learning pathway.
                  </p>
                  <p>
                    <strong>Bonus:</strong> Retake this assessment as you progress through the app
                    to see how your philosophical thinking evolves over time.
                  </p>
                </div>
              </div>

              {previousResults.length > 0 && (
                <div className="bg-purple-900/30 backdrop-blur rounded-xl p-6 border border-purple-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-bold">Your Previous Results</h3>
                  </div>
                  <p className="text-sm text-purple-200 mb-3">
                    Last assessment: {new Date(previousResults[0].date).toLocaleDateString()}
                  </p>
                  <p className="text-purple-100">
                    Your primary profile was: <strong>{previousResults[0].topProfile.title}</strong>
                  </p>
                  <p className="text-sm text-purple-300 mt-2">
                    Retake the assessment to see how you've grown.
                  </p>
                </div>
              )}

              <button
                onClick={() => setView('questions')}
                className="w-full px-8 py-4 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center gap-2"
              >
                Begin Assessment
                <ArrowRight className="w-6 h-6" />
              </button>

              <p className="text-center text-sm text-purple-300">
                Takes approximately 5 minutes · {QUESTIONS.length} questions
              </p>
            </motion.div>
          )}

          {/* Questions View */}
          {view === 'questions' && (
            <motion.div
              key="questions"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Announces each advance to screen readers; re-renders on change */}
              <div className="sr-only" role="status">
                Question {currentQuestionIndex + 1} of {QUESTIONS.length}
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-purple-300">
                    Question {currentQuestionIndex + 1} of {QUESTIONS.length}
                  </span>
                  <span className="text-sm text-purple-300">
                    {Math.round(progress)}% Complete
                  </span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                  />
                </div>
              </div>

              {/* Question Card */}
              <motion.div
                key={currentQuestion.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-purple-500/30"
              >
                <h2 ref={questionHeadingRef} tabIndex={-1} className="text-2xl font-bold mb-6 text-purple-100">
                  {currentQuestion.text}
                </h2>

                <div className="space-y-3">
                  {currentQuestion.options.map((option, index) => (
                    <motion.button
                      key={index}
                      onClick={() => answerQuestion(index)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full p-4 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-purple-500 rounded-lg text-left transition-all"
                    >
                      <span className="text-purple-100">{option.text}</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              {/* Navigation */}
              {currentQuestionIndex > 0 && (
                <button
                  onClick={goBack}
                  className="flex items-center gap-2 px-4 py-2 text-purple-300 hover:text-purple-100 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous Question
                </button>
              )}
            </motion.div>
          )}

          {/* Results View */}
          {view === 'results' && results.length > 0 && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Primary Profile */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.6, delay: 0.2 }}
                  className="flex justify-center mb-6"
                >
                  {React.createElement(results[0].icon, {
                    className: `w-24 h-24 text-${results[0].color}-400`
                  })}
                </motion.div>
                <motion.h1
                  ref={resultsHeadingRef}
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-5xl font-bold mb-4"
                >
                  You're {results[0].title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-xl text-purple-200 max-w-2xl mx-auto"
                >
                  {results[0].description}
                </motion.p>
              </div>

              {/* Your Strengths */}
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
                <h2 className="text-2xl font-bold mb-4">Your Philosophical Strengths</h2>
                <div className="grid grid-cols-2 gap-3">
                  {results[0].strengths.map((strength, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + idx * 0.1 }}
                      className="flex items-center gap-2 bg-slate-700/30 rounded-lg p-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-purple-100">{strength}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Complete Profile Breakdown */}
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
                <h2 className="text-2xl font-bold mb-4">Your Complete Philosophical Profile</h2>
                <p className="text-purple-200 mb-6">
                  How each philosophical domain resonates with you, relative to your strongest:
                </p>
                <div className="space-y-4">
                  {results.map((profile, idx) => {
                    const Icon = profile.icon;
                    return (
                      <motion.div
                        key={profile.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + idx * 0.1 }}
                        className="space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Icon className={`w-6 h-6 text-${profile.color}-400`} />
                            <span className="font-semibold">{profile.title}</span>
                          </div>
                          <span className="text-purple-300">{profile.percentage}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${profile.percentage}%` }}
                            transition={{ delay: 0.7 + idx * 0.1, duration: 0.5 }}
                            className={`h-full bg-${profile.color}-500`}
                          />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Top 3 Recommendations */}
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
                <h2 className="text-2xl font-bold mb-4">Your Personalised Learning Pathway</h2>
                <p className="text-purple-200 mb-6">
                  Based on your profile, these are the places to start your philosophical journey:
                </p>
                <div className="space-y-6">
                  {results.slice(0, 3).map((profile, idx) => {
                    const Icon = profile.icon;
                    return (
                      <motion.div
                        key={profile.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 + idx * 0.15 }}
                        className="bg-slate-700/30 rounded-lg p-6"
                      >
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`p-3 bg-${profile.color}-900/30 rounded-lg`}>
                            <Icon className={`w-8 h-8 text-${profile.color}-400`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold mb-1">{profile.title}</h3>
                            <p className="text-sm text-purple-300">{profile.module}</p>
                          </div>
                          <span className={`px-3 py-1 bg-${profile.color}-900/50 text-${profile.color}-300 rounded-full text-sm font-medium`}>
                            {idx === 0 ? 'Start Here' : idx === 1 ? 'Then Try' : 'Also Explore'}
                          </span>
                        </div>

                        <div className="space-y-3 mb-4">
                          <div>
                            <h4 className="text-sm font-semibold text-purple-300 mb-2">Key Questions You'll Explore:</h4>
                            <ul className="space-y-1">
                              {profile.keyQuestions.map((question, qIdx) => (
                                <li key={qIdx} className="text-sm text-purple-100 flex items-start gap-2">
                                  <span className="text-purple-400 mt-1">•</span>
                                  <span>{question}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-purple-300 mb-2">Recommended Tools:</h4>
                            <div className="flex flex-wrap gap-2">
                              {profile.recommendedTools.map((tool, tIdx) => (
                                <span
                                  key={tIdx}
                                  className="px-3 py-1 bg-slate-800/50 border border-slate-600 rounded-lg text-sm text-purple-100"
                                >
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Growth Tracking */}
              {previousResults.length > 1 && (
                <div className="bg-purple-900/30 backdrop-blur rounded-xl p-6 border border-purple-500/30">
                  <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                    Your Philosophical Evolution
                  </h2>
                  <p className="text-purple-200 mb-3">
                    Last time you took this assessment ({new Date(previousResults[1].date).toLocaleDateString()}),
                    your primary profile was <strong>{previousResults[1].topProfile.title}</strong>.
                  </p>
                  {previousResults[0].topProfile.id !== previousResults[1].topProfile.id ? (
                    <p className="text-purple-100">
                      Your thinking has evolved. You've shifted from {previousResults[1].topProfile.title} to {results[0].title}.
                      This shows your philosophical interests are growing and changing as you learn.
                    </p>
                  ) : (
                    <p className="text-purple-100">
                      You're consistently a {results[0].title}, but notice how your secondary and tertiary
                      profiles may have shifted as your thinking develops.
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={exportResults}
                  className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Export Results
                </button>
                <button
                  onClick={resetAssessment}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  Retake Assessment
                </button>
              </div>

              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30 text-center">
                <p className="text-purple-200">
                  <strong>Remember:</strong> Your philosophical profile isn't fixed. As you explore different
                  modules and engage with new ideas, your thinking will evolve. Retake this assessment
                  periodically to track your growth.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PhilosophicalProfileAssessment;
